import {engineContext} from './context.js';

(function(g){'use strict';
const VERSION='0.1.0-alpha';
const DATASET='market-daily';

function num(v){
  const n=Number(v);
  return Number.isFinite(n)?n:null;
}

function parseDaily(json,symbol){
  if(!json||typeof json!=='object') throw Error('MARKET_PAYLOAD_REQUIRED');
  if(json['Error Message']) throw Error('PROVIDER_SYMBOL_ERROR');
  if(json['Note']) throw Error('PROVIDER_RATE_LIMIT');
  if(json['Information']) throw Error('PROVIDER_INFORMATION');

  const meta=json['Meta Data']||{};
  const series=json['Time Series (Daily)']||{};
  const rows=Object.entries(series).map(([date,v])=>({
    date,
    open:num(v['1. open']),
    high:num(v['2. high']),
    low:num(v['3. low']),
    close:num(v['4. close']),
    volume:num(v['5. volume'])
  })).filter(r=>r.date).sort((a,b)=>b.date.localeCompare(a.date));

  return {
    symbol:symbol||meta['2. Symbol']||null,
    timezone:meta['5. Time Zone']||null,
    lastRefreshed:meta['3. Last Refreshed']||null,
    rows
  };
}

function create(opts={}){
  const fetchFn=opts.fetchFn||g.fetch;
  const apiKey=opts.apiKey||null;
  const endpoint=opts.endpoint||'https://www.alphavantage.co/query';

  async function fetchDaily(req={}){
    const symbol=String(req.symbol||'').trim().toUpperCase();
    if(!symbol) throw Error('SYMBOL_REQUIRED');
    if(!apiKey) throw Error('API_KEY_REQUIRED');
    if(typeof fetchFn!=='function') throw Error('FETCH_REQUIRED');

    const outputsize=req.outputsize==='full'?'full':'compact';
    const url=endpoint+
      '?function=TIME_SERIES_DAILY&symbol='+encodeURIComponent(symbol)+
      '&outputsize='+outputsize+
      '&apikey='+encodeURIComponent(apiKey);

    const res=await fetchFn(url,{headers:{Accept:'application/json'}});
    if(!res||!res.ok) throw Error(`MARKET_HTTP_${res&&res.status||'ERROR'}`);

    const json=await res.json();
    const parsed=parseDaily(json,symbol);
    const latest=parsed.rows[0]||null;

    return {
      symbol,
      asOf:latest?latest.date:null,
      receivedAt:new Date().toISOString(),
      state:latest?'VERIFIED':'MISSING',
      provenance:{
        provider:'Alpha Vantage',
        dataset:DATASET,
        function:'TIME_SERIES_DAILY',
        outputsize,
        endpoint
      },
      payload:parsed
    };
  }

  return Object.freeze({
    name:'ALPHA_VANTAGE',
    datasets:Object.freeze([DATASET]),
    fetch:fetchDaily
  });
}

async function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});
  const fixture={
    'Meta Data':{
      '2. Symbol':'AAA',
      '3. Last Refreshed':'2026-08-29',
      '5. Time Zone':'US/Eastern'
    },
    'Time Series (Daily)':{
      '2026-08-29':{'1. open':'101.5','2. high':'104','3. low':'100','4. close':'103.25','5. volume':'123456'},
      '2026-08-28':{'1. open':'99','2. high':'102','3. low':'98.5','4. close':'101','5. volume':'100000'}
    }
  };

  const p=parseDaily(fixture,'AAA');
  ok('symbol parsed',p.symbol==='AAA');
  ok('timezone parsed',p.timezone==='US/Eastern');
  ok('rows parsed',p.rows.length===2);
  ok('rows sorted newest first',p.rows[0].date==='2026-08-29');
  ok('close numeric',p.rows[0].close===103.25);
  ok('volume numeric',p.rows[0].volume===123456);

  let requested='';
  const adapter=create({apiKey:'TEST_KEY',fetchFn:async url=>{requested=url;return {ok:true,status:200,json:async()=>fixture}}});
  ok('adapter name declared',adapter.name==='ALPHA_VANTAGE');
  ok('dataset declared',adapter.datasets[0]===DATASET);

  const r=await adapter.fetch({symbol:'aaa'});
  ok('symbol normalized',r.symbol==='AAA');
  ok('verified when rows exist',r.state==='VERIFIED');
  ok('asOf latest trading date',r.asOf==='2026-08-29');
  ok('request uses daily function',requested.includes('function=TIME_SERIES_DAILY'));
  ok('request uses compact by default',requested.includes('outputsize=compact'));
  ok('provenance provider retained',r.provenance.provider==='Alpha Vantage');

  const full=create({apiKey:'K',fetchFn:async url=>({ok:true,status:200,json:async()=>fixture})});
  const rf=await full.fetch({symbol:'AAA',outputsize:'full'});
  ok('full output accepted',rf.provenance.outputsize==='full');

  const empty=create({apiKey:'K',fetchFn:async()=>({ok:true,status:200,json:async()=>({'Meta Data':{},'Time Series (Daily)':{}})})});
  const e=await empty.fetch({symbol:'AAA'});
  ok('empty series remains missing',e.state==='MISSING');
  ok('empty series has no asOf',e.asOf===null);

  let keyErr=false;try{await create({fetchFn:async()=>({})}).fetch({symbol:'AAA'})}catch(e){keyErr=e.message==='API_KEY_REQUIRED'}
  ok('api key required',keyErr);

  let symErr=false;try{await adapter.fetch({symbol:''})}catch(e){symErr=e.message==='SYMBOL_REQUIRED'}
  ok('symbol required',symErr);

  let httpErr=false;try{await create({apiKey:'K',fetchFn:async()=>({ok:false,status:503})}).fetch({symbol:'AAA'})}catch(e){httpErr=e.message==='MARKET_HTTP_503'}
  ok('http error explicit',httpErr);

  let rate=false;try{parseDaily({Note:'rate'})}catch(e){rate=e.message==='PROVIDER_RATE_LIMIT'}
  ok('rate limit explicit',rate);

  let symbolError=false;try{parseDaily({'Error Message':'bad'})}catch(e){symbolError=e.message==='PROVIDER_SYMBOL_ERROR'}
  ok('provider symbol error explicit',symbolError);

  const api=create({apiKey:'K',fetchFn:async()=>({ok:true,json:async()=>fixture})});
  ok('no trade signal',!('tradeSignal' in api));
  ok('no capital permission',!('capitalPermission' in api));
  ok('no universal score',!('score' in api));

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

g.ZooInvestAlphaVantageDailyProviderAdapter=Object.freeze({VERSION,DATASET,parseDaily,create,selfTest});
})(engineContext);